export interface IntegrityMonitorIdentifier {
  _id: string;
  name: string;
}

export enum IntegrityMonitorEntityType {
  FILE = 1,
  DIRECTORY,
  REGISTRYKEY,
  REGISTRYVALUE,
  SOFTWARE,
  PROCESS,
  PORT,
  SERVICE,
  USERS,
  GROUPS,
  SHARES,
}

export function getIntegrityMonitorTypeValues(translateFn) {
  return [
    {
      label: translateFn('File'),
      value: IntegrityMonitorEntityType.FILE,
    },
    {
      label: translateFn('Directory'),
      value: IntegrityMonitorEntityType.DIRECTORY,
    },
    {
      label: translateFn('Registry Key'),
      value: IntegrityMonitorEntityType.REGISTRYKEY,
    },
    {
      label: translateFn('Registry Value'),
      value: IntegrityMonitorEntityType.REGISTRYVALUE,
    },
    {
      label: translateFn('Installed Software'),
      value: IntegrityMonitorEntityType.SOFTWARE,
    },
    {
      label: translateFn('Service'),
      value: IntegrityMonitorEntityType.SERVICE,
    },
    {
      label: translateFn('User'),
      value: IntegrityMonitorEntityType.USERS,
    },
  ];
}

export enum IntegrityMonitorSeverity {
  LOW = 1,
  MEDIUM,
  HIGH,
  CRITICAL,
}

export function getIntegrityMonitorSeverityValues(translateFn) {
  return [
    {
      label: translateFn('Low'),
      value: IntegrityMonitorSeverity.LOW,
    },
    {
      label: translateFn('Medium'),
      value: IntegrityMonitorSeverity.MEDIUM,
    },
    {
      label: translateFn('High'),
      value: IntegrityMonitorSeverity.HIGH,
    },
    {
      label: translateFn('Critical'),
      value: IntegrityMonitorSeverity.CRITICAL,
    },
  ];
}

export enum IntegrityMonitorPlatform {
  WINDOWS = 1,
  LINUX,
  OSX,
}

export function getIntegrityMonitorOsTypes(translateFn) {
  return [
    {
      label: translateFn('Windows'),
      value: IntegrityMonitorPlatform.WINDOWS,
      implemented: true,
    },
    {
      label: translateFn('Linux'),
      value: IntegrityMonitorPlatform.LINUX,
      implemented: true,
    },
    {
      label: translateFn('macOS'),
      value: IntegrityMonitorPlatform.OSX,
      implemented: false,
    },
  ];
}

export function getIntegrityMonitorRulesActionsLabels(translateFn: Function) {
  return {
    [IntegrityMonitorRulesActionValues.RESTORE_ATTRIBUTES]: translateFn('Correct'),
    [IntegrityMonitorRulesActionValues.RESTORE_GROUP]: translateFn('Correct'),
    [IntegrityMonitorRulesActionValues.RESTORE_OWNER]: translateFn('Correct'),
    [IntegrityMonitorRulesActionValues.RESTORE_PERMISSIONS]: translateFn('Correct'),
    [IntegrityMonitorRulesActionValues.RESTORE_VALUE]: translateFn('Correct'),
    [IntegrityMonitorRulesActionValues.MOVE_TO_QUARANTINE]: translateFn('Move to quarantine'),
    [IntegrityMonitorRulesActionValues.DELETE]: translateFn('Delete'),
  };
}

export interface IntegrityMonitorCustomRulesAttribute {
  label: string;
  value: any;
  actions: Array<{
    label: string;
    value: any;
  }>;
  excluded?: IntegrityMonitorPlatform[];
}

export enum IntegrityMonitorRulesAttributeValues {
  CREATED = 'created',
  DELETED = 'deleted',
  RENAMED = 'renamed',

  LAST_MODIFIED = 'last_modified',
  HASH_CHANGED = 'hash',
  SIZE_CHANGED = 'size',

  SUBKEYS_CHANGED = 'subkeys',
  VALUES_CHANGED = 'values',

  ATTRIBUTES_CHANGED = 'attributes',

  PERMISSION_CHANGED = 'permissions',
  OWNER_CHANGED = 'owner',
  GROUP_CHANGED = 'group',
}

export enum IntegrityMonitorRulesActionValues {
  RESTORE_ATTRIBUTES = 'restore_object_attributes',
  RESTORE_GROUP = 'restore_object_group',
  RESTORE_OWNER = 'restore_object_owner',
  RESTORE_PERMISSIONS = 'restore_object_permissions',
  RESTORE_VALUE = 'restore_object_value',
  DELETE = 'delete_object',
  MOVE_TO_QUARANTINE = 'quarantine_object',
}

export enum IntegrityMonitorRulesActionResults {
  SUCCESSFUL,
  FAILED,
  PENDING,
}

export function getIntegrityMonitorRulesDirectoryAttributes(translateFn): IntegrityMonitorCustomRulesAttribute[] {
  const actionLabels = getIntegrityMonitorRulesActionsLabels(translateFn);

  return [
    {
      label: translateFn('Directory was created'),
      value: IntegrityMonitorRulesAttributeValues.CREATED,
      actions: [],
    },
    {
      label: translateFn('Directory was deleted'),
      value: IntegrityMonitorRulesAttributeValues.DELETED,
      actions: [],
    },
    {
      label: translateFn('Directory was renamed'),
      value: IntegrityMonitorRulesAttributeValues.RENAMED,
      actions: [],
    },

    {
      label: translateFn('Directory attributes were changed'),
      value: IntegrityMonitorRulesAttributeValues.ATTRIBUTES_CHANGED,
      actions: [
        {
          label: actionLabels[IntegrityMonitorRulesActionValues.RESTORE_ATTRIBUTES],
          value: IntegrityMonitorRulesActionValues.RESTORE_ATTRIBUTES,
        },
      ],
      excluded: [IntegrityMonitorPlatform.LINUX, IntegrityMonitorPlatform.OSX],
    },

    {
      label: translateFn('Directory permissions were changed'),
      value: IntegrityMonitorRulesAttributeValues.PERMISSION_CHANGED,
      actions: [
        {
          label: actionLabels[IntegrityMonitorRulesActionValues.RESTORE_PERMISSIONS],
          value: IntegrityMonitorRulesActionValues.RESTORE_PERMISSIONS,
        },
      ],
      excluded: [IntegrityMonitorPlatform.WINDOWS],
    },
    {
      label: translateFn('Directory owner was changed'),
      value: IntegrityMonitorRulesAttributeValues.OWNER_CHANGED,
      actions: [
        {
          label: actionLabels[IntegrityMonitorRulesActionValues.RESTORE_OWNER],
          value: IntegrityMonitorRulesActionValues.RESTORE_OWNER,
        },
      ],
      excluded: [IntegrityMonitorPlatform.WINDOWS],
    },
    {
      label: translateFn('Directory group was changed'),
      value: IntegrityMonitorRulesAttributeValues.GROUP_CHANGED,
      actions: [
        {
          label: actionLabels[IntegrityMonitorRulesActionValues.RESTORE_GROUP],
          value: IntegrityMonitorRulesActionValues.RESTORE_GROUP,
        },
      ],
      excluded: [IntegrityMonitorPlatform.WINDOWS],
    },
  ];
}

export function getIntegrityMonitorRulesFileAttributes(translateFn): IntegrityMonitorCustomRulesAttribute[] {
  const actionLabels = getIntegrityMonitorRulesActionsLabels(translateFn);

  return [
    {
      label: translateFn('File was created'),
      value: IntegrityMonitorRulesAttributeValues.CREATED,
      actions: [
        {
          label: actionLabels[IntegrityMonitorRulesActionValues.DELETE],
          value: IntegrityMonitorRulesActionValues.DELETE,
        },
        {
          label: actionLabels[IntegrityMonitorRulesActionValues.MOVE_TO_QUARANTINE],
          value: IntegrityMonitorRulesActionValues.MOVE_TO_QUARANTINE,
        },
      ],
    },
    {
      label: translateFn('File was last modified'),
      value: IntegrityMonitorRulesAttributeValues.LAST_MODIFIED,
      actions: [],
    },
    {
      label: translateFn('File was deleted'),
      value: IntegrityMonitorRulesAttributeValues.DELETED,
      actions: [],
    },
    {
      label: translateFn('File was renamed'),
      value: IntegrityMonitorRulesAttributeValues.RENAMED,
      actions: [],
    },
    {
      label: translateFn('File hash was changed'),
      value: IntegrityMonitorRulesAttributeValues.HASH_CHANGED,
      actions: [],
    },
    {
      label: translateFn('File size was changed'),
      value: IntegrityMonitorRulesAttributeValues.SIZE_CHANGED,
      actions: [],
    },

    {
      label: translateFn('File attributes were changed'),
      value: IntegrityMonitorRulesAttributeValues.ATTRIBUTES_CHANGED,
      actions: [
        {
          label: actionLabels[IntegrityMonitorRulesActionValues.RESTORE_ATTRIBUTES],
          value: IntegrityMonitorRulesActionValues.RESTORE_ATTRIBUTES,
        },
      ],
      excluded: [IntegrityMonitorPlatform.LINUX, IntegrityMonitorPlatform.OSX],
    },

    {
      label: translateFn('File permissions were changed'),
      value: IntegrityMonitorRulesAttributeValues.PERMISSION_CHANGED,
      actions: [
        {
          label: actionLabels[IntegrityMonitorRulesActionValues.RESTORE_PERMISSIONS],
          value: IntegrityMonitorRulesActionValues.RESTORE_PERMISSIONS,
        },
      ],
      excluded: [IntegrityMonitorPlatform.WINDOWS],
    },
    {
      label: translateFn('File owner was changed'),
      value: IntegrityMonitorRulesAttributeValues.OWNER_CHANGED,
      actions: [
        {
          label: actionLabels[IntegrityMonitorRulesActionValues.RESTORE_OWNER],
          value: IntegrityMonitorRulesActionValues.RESTORE_OWNER,
        },
      ],
      excluded: [IntegrityMonitorPlatform.WINDOWS],
    },
    {
      label: translateFn('File group was changed'),
      value: IntegrityMonitorRulesAttributeValues.GROUP_CHANGED,
      actions: [
        {
          label: actionLabels[IntegrityMonitorRulesActionValues.RESTORE_GROUP],
          value: IntegrityMonitorRulesActionValues.RESTORE_GROUP,
        },
      ],
      excluded: [IntegrityMonitorPlatform.WINDOWS],
    },
  ];
}

export function getIntegrityMonitorRulesRegistryKeyAttributes(translateFn): IntegrityMonitorCustomRulesAttribute[] {
  return [
    {
      label: translateFn('Registry key was created'),
      value: IntegrityMonitorRulesAttributeValues.CREATED,
      actions: [],
    },
    {
      label: translateFn('Registry key was deleted'),
      value: IntegrityMonitorRulesAttributeValues.DELETED,
      actions: [],
    },
    {
      label: translateFn('Registry subkeys were changed'),
      value: IntegrityMonitorRulesAttributeValues.SUBKEYS_CHANGED,
      actions: [],
    },
    {
      label: translateFn('Registry key values were changed'),
      value: IntegrityMonitorRulesAttributeValues.VALUES_CHANGED,
      actions: [],
    },
    //{
    //  label: translateFn('Registry key owner was changed'),
    //  value: IntegrityMonitorRulesAttributeValues.OWNER_CHANGED,
    //  actions: [
    //    {
    //      label: actionLabels[IntegrityMonitorRulesActionValues.RESTORE_OWNER],
    //      value: IntegrityMonitorRulesActionValues.RESTORE_OWNER,
    //    },
    //  ],
    //},
    //{
    //  label: translateFn('Registry key permissions were changed'),
    //  value: IntegrityMonitorRulesAttributeValues.PERMISSION_CHANGED,
    //  actions: [
    //    {
    //      label: actionLabels[IntegrityMonitorRulesActionValues.RESTORE_PERMISSIONS],
    //      value: IntegrityMonitorRulesActionValues.RESTORE_PERMISSIONS,
    //    },
    //  ],
    //},
  ];
}

export function getIntegrityMonitorRulesRegistryValueAttributes(translateFn): IntegrityMonitorCustomRulesAttribute[] {
  const actionLabels = getIntegrityMonitorRulesActionsLabels(translateFn);

  return [
    {
      label: translateFn('Registry value was created'),
      value: IntegrityMonitorRulesAttributeValues.CREATED,
      actions: [],
    },
    {
      label: translateFn('Registry value was deleted'),
      value: IntegrityMonitorRulesAttributeValues.DELETED,
      actions: [],
    },
    {
      label: translateFn('Registry value was last modified'),
      value: IntegrityMonitorRulesAttributeValues.LAST_MODIFIED,
      actions: [],
    },
    {
      label: translateFn('Registry value hash was changed'),
      value: IntegrityMonitorRulesAttributeValues.HASH_CHANGED,
      actions: [
        {
          label: actionLabels[IntegrityMonitorRulesActionValues.RESTORE_VALUE],
          value: IntegrityMonitorRulesActionValues.RESTORE_VALUE,
        },
      ],
    },
    {
      label: translateFn('Registry value size was changed'),
      value: IntegrityMonitorRulesAttributeValues.SIZE_CHANGED,
      actions: [
        {
          label: actionLabels[IntegrityMonitorRulesActionValues.DELETE],
          value: IntegrityMonitorRulesActionValues.DELETE,
        },
      ],
    },
  ];
}
